import { useLogHideModeSwitch } from "@vidario/api-client";
import { ConfirmDialog, Switch, useToast } from "@vidario/ui";
import { Eye, EyeOff } from "lucide-react";
import { useEffect, useState, type ReactElement } from "react";
import { useSearchParams } from "react-router";

import { anonymView } from "../platform/anonym-view";
import { useAnonymView } from "./useAnonymView";

const TITLE_PREFIX = "Anonym · ";

/** The Anonym-Ansicht switch in the TopNav (#140, admins only). Switching on
 * is one click; switching off asks first, because one stray click while
 * sharing a screen would show everything. Both are logged. */
export function AnonymToggle(): ReactElement {
  const on = useAnonymView();
  const [confirmOff, setConfirmOff] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const log = useLogHideModeSwitch();
  const toast = useToast();
  const [, setSearchParams] = useSearchParams();

  // The browser tab says so too, e.g. in a screen-share's window picker.
  useEffect(() => {
    const base = document.title.replace(TITLE_PREFIX, "");
    document.title = on ? `${TITLE_PREFIX}${base}` : base;
    return () => {
      document.title = document.title.replace(TITLE_PREFIX, "");
    };
  }, [on]);

  const turn = (next: boolean): void => {
    anonymView.set(next);
    log.mutate(next, {
      onError: () =>
        toast.push({
          kind: "warning",
          title: "Umschalten wurde nicht protokolliert",
          message: "Bitte gib dem Team Bescheid.",
        }),
    });
    // A name typed into a search field would stay on screen.
    if (next) {
      setSearchParams(
        (params) => {
          params.delete("search");
          return params;
        },
        { replace: true },
      );
    }
    setAnnouncement(next ? "Anonym-Ansicht eingeschaltet" : "Anonym-Ansicht ausgeschaltet");
  };

  return (
    <>
      <Switch
        checked={on}
        onCheckedChange={(next) => (next ? turn(true) : setConfirmOff(true))}
        aria-label="Anonym-Ansicht"
        // `cn` doesn't resolve conflicting classes, so each state brings its own colours.
        className={
          on
            ? "h-9 bg-hide-mode px-2 text-hide-mode-fg sm:px-3"
            : "h-9 px-2 text-neutral-700 hover:bg-neutral-50 sm:px-3"
        }
      >
        {/* Phones show only the track: the header has no room for more. */}
        <span className="hidden sm:inline-flex">
          {on ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
        </span>
        <span className="hidden xl:inline">Anonym</span>
      </Switch>
      <p role="status" className="sr-only">
        {announcement}
      </p>
      <ConfirmDialog
        open={confirmOff}
        onOpenChange={setConfirmOff}
        title="Klardaten wieder anzeigen?"
        confirmLabel="Klardaten anzeigen"
        initialFocus="cancel"
        onConfirm={() => {
          setConfirmOff(false);
          turn(false);
        }}
      >
        Namen, Kontaktdaten, Wohnorte, Arbeitgeber und Notizen werden wieder angezeigt. Wenn du
        deinen Bildschirm teilst, sehen alle Teilnehmer sie.
      </ConfirmDialog>
    </>
  );
}

/** The dark strip above the TopNav while the Anonym-Ansicht is on. It sticks
 * with the nav, so it stays visible while the page scrolls. */
export function AnonymBar(): ReactElement | null {
  const on = useAnonymView();
  // Fixed overlays below the nav (the WhatsNew notice on phones) move down with it.
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--anonym-bar-offset",
      on ? "var(--hide-mode-bar-height)" : "0px",
    );
  }, [on]);
  if (!on) return null;
  return (
    <div className="flex h-(--hide-mode-bar-height) items-center justify-center truncate bg-hide-mode px-4 text-xs font-medium text-hide-mode-fg">
      Anonym-Ansicht aktiv
      <span className="hidden sm:inline">
        &nbsp;· Namen, Kontaktdaten, Wohnorte und Arbeitgeber sind verborgen
      </span>
    </div>
  );
}
